<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { BaseModal, BaseCircleButton, BaseButton } from '@/components/Base';
import { EmergencySituationInstructions } from '@/enums/EmergencySituationInstructions';

const { t } = useI18n();

const props = defineProps<{
  instruction: EmergencySituationInstructions;
}>();

const emit = defineEmits<{
  close: []
}>();

const modalTitle = computed(() => {
  switch (props.instruction) {
    case EmergencySituationInstructions.Cpr:
      return t('instructions.cprTitle');
    case EmergencySituationInstructions.CameraInstructionsIos:
    case EmergencySituationInstructions.CameraInstructionsAndroid:
      return t('instructions.cameraTitle');
    case EmergencySituationInstructions.GpsInstructionsIos:
    case EmergencySituationInstructions.GpsInstructionsAndroid:
      return t('instructions.gpsTitle');
    default:
      return t('instructions.title');
  }
});

const instructionImages = computed((): string[] => {
  switch (props.instruction) {
    case EmergencySituationInstructions.CameraInstructionsIos:
      return [
        '/instructions/camera/iOS-1.jpg',
        '/instructions/camera/iOS-2.jpg',
        '/instructions/camera/iOS-3.jpg',
        '/instructions/camera/iOS-4.jpg',
        '/instructions/camera/iOS-5.jpg',
      ];
    case EmergencySituationInstructions.CameraInstructionsAndroid:
      return [
        '/instructions/camera/Android-1.jpg',
        '/instructions/camera/Android-2.jpg',
        '/instructions/camera/Android-3.jpg',
        '/instructions/camera/Android-4.jpg',
        '/instructions/camera/Android-5.jpg',
        '/instructions/camera/Android-6.jpg',
        '/instructions/camera/Android-7.jpg',
      ];
    case EmergencySituationInstructions.GpsInstructionsIos:
      return [
        '/instructions/gps/iOS-1.jpg',
        '/instructions/gps/iOS-2.jpg',
        '/instructions/gps/iOS-3.jpg',
        '/instructions/gps/iOS-4.jpg',
        '/instructions/gps/iOS-5.jpg',
      ];
    case EmergencySituationInstructions.GpsInstructionsAndroid:
      return [
        '/instructions/gps/Android-1.jpg',
        '/instructions/gps/Android-2.jpg',
        '/instructions/gps/Android-3.jpg',
        '/instructions/gps/Android-4.jpg',
        '/instructions/gps/Android-5.jpg',
      ];
    case EmergencySituationInstructions.Choking:
      return [
        '/instructions/FirstAid/firstaidinfo_duseni_step1.png',
        '/instructions/FirstAid/firstaidinfo_duseni_step2a.png',
        '/instructions/FirstAid/firstaidinfo_duseni_step2b.png',
        '/instructions/FirstAid/firstaidinfo_duseni_step3a.png',
      ];
    case EmergencySituationInstructions.ChildChoking:
      return [
        '/instructions/FirstAid/firstaidinfo_duseni_step4a.png',
        '/instructions/FirstAid/firstaidinfo_duseni_step4b.png',
      ];
    default:
      return [];
  }
});

const hasImages = computed(() => instructionImages.value.length > 0);

const isCprInstruction = computed(() => props.instruction === EmergencySituationInstructions.Cpr);

const cprVideoUrl = '/videos/cpr-short.mp4';
</script>

<template>
  <BaseModal @close="emit('close')">
    <div class="instructions-modal__content">
      <div class="instructions-modal__header">
        <h2 class="instructions-modal__title">{{ modalTitle }}</h2>
        <BaseCircleButton
          class="instructions-modal__close"
          icon="close"
          :size="36"
          :icon-size="20"
          @click="emit('close')"
        />
      </div>

      <div v-if="isCprInstruction" class="instructions-modal__body instructions-modal__body--video">
        <video
          class="instructions-modal__video"
          :src="cprVideoUrl"
          autoplay
          loop
          playsinline
        ></video>
      </div>

      <div v-else-if="hasImages" class="instructions-modal__body">
        <ol class="instructions-modal__steps">
          <li v-for="(image, index) in instructionImages" :key="index" class="instructions-modal__step">
            <span class="instructions-modal__step-number">{{ index + 1 }}</span>
            <img
              :src="image"
              class="instructions-modal__step-image"
              :alt="$t('instructions.step', [index + 1])"
            />
          </li>
        </ol>
      </div>

      <div class="instructions-modal__footer">
        <BaseButton @click="emit('close')">
          {{ t('instructions.close') }}
        </BaseButton>
      </div>
    </div>
  </BaseModal>
</template>

<style scoped>
.instructions-modal__content {
  background-color: #ffffff;
  border-radius: var(--border-radius-lg, 12px);
  max-width: 90%;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.instructions-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  border-bottom: 1px solid #e5e5e5;
  background-color: #f8f9fa;
}

.instructions-modal__title {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: #1a1a1a;
}

.instructions-modal__close {
  flex-shrink: 0;
}

.instructions-modal__body {
  padding: var(--spacing-md);
  overflow-y: auto;
  background-color: #ffffff;
}

.instructions-modal__steps {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.instructions-modal__step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
}

.instructions-modal__step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: var(--color-primary);
  color: #ffffff;
  border-radius: 50%;
  font-size: var(--font-size-md);
  font-weight: 600;
}

.instructions-modal__step-image {
  width: 100%;
  height: auto;
  border-radius: var(--border-radius);
  border: 1px solid #e5e5e5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.instructions-modal__footer {
  padding: var(--spacing-md);
  border-top: 1px solid #e5e5e5;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
}

.instructions-modal__body--video {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
}

.instructions-modal__video {
  width: 100%;
  max-height: 60vh;
  object-fit: contain;
}
</style>
